import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { Heading } from '@acb_devel/design-system';
import { Text } from '@acb_devel/design-system';
import CardBackground from '@assets/images/card-background.webp';
import { getEvents } from '@services/events';
import type { Event } from '@services/events';
import { Alert } from '@acb_devel/design-system';
import { Button } from '@acb_devel/design-system';
import { AngleUpIcon } from '@acb_devel/design-system';
import { AngleDownIcon } from '@acb_devel/design-system';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from '@acb_devel/design-system';
import { Checkbox } from '@acb_devel/design-system';
import { createRegistration } from '@services/registrations';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';
import { useEventsContext } from '@contexts/EventsContext';
import { useRegistrationsContext } from '@contexts/RegistrationsContext';
import { printEventDate } from '@utils/date';

import styles from './EventsAvailableCard.module.scss';

import CopaDelReyLogo from '@assets/images/events-logos/copa-del-rey/copa-del-rey-primary.svg';
import GamedayLogo from '@assets/images/events-logos/gameday/gameday-primary.svg';
import LaJaulaLogo from '@assets/images/events-logos/la-jaula/la-jaula-full-white-horizontal.svg';
import LigaEndesaLogo from '@assets/images/events-logos/liga-endesa/liga-endesa-white-2lines.svg';
import MinicopaLogo from '@assets/images/events-logos/minicopa/minicopa-white.svg';
import NavidadACBLogo from '@assets/images/events-logos/navidad-acb/navidad-acb-white.svg';
import PlayoffLogo from '@assets/images/events-logos/playoff/playoff-white.svg';
import SupercopaLogo from '@assets/images/events-logos/supercopa/supercopa-primary.svg';
import DefaultLogo from '@assets/images/events-logos/default-acb/acb-logo-white.svg';

const IMAGES_MAP = {
  'Copa del Rey': CopaDelReyLogo,
  'Supercopa Endesa': SupercopaLogo,
  'Fase previa Minicopa': MinicopaLogo,
  'Playoff Final Liga Endesa': PlayoffLogo,
  'GameDay Movistar': GamedayLogo,
  'La Jaula by Movistar': LaJaulaLogo,
  'Presentación Temporada': LigaEndesaLogo,
  'Campaña Navidad acb': NavidadACBLogo,
  'Sorteo Copa del Rey': CopaDelReyLogo,
  'Circuito Pretemporada Movistar': LigaEndesaLogo,
  'Primera Fase de la Minicopa Endesa': MinicopaLogo,
} as Record<string, string>;

export function EventsAvailableCard({ className }: { className?: string }) {
  const { isOpen, openModal, closeModal } = useModal();
  const {
    isOpen: isOpenConfirm,
    openModal: openModalConfirm,
    closeModal: closeModalConfirm,
  } = useModal();
  const { addToast } = useToastContext();

  const [isLoading, setIsLoading] = useState(true);
  const { events, setEvents } = useEventsContext();
  const { registrations, setRegistrations } = useRegistrationsContext();
  const [mobileEventsOpen, setMobileEventsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState({} as Event);
  const [selectedAreas, setSelectedAreas] = useState([] as number[]);
  const [selectedAreasError, setSelectedAreasError] = useState('');

  useEffect(() => {
    getEvents({ open_for_registration: true, ordering: 'start_event' })
      .then(({ results }) => {
        setEvents(results);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setSelectedAreas([]);

    if (event.eventAreas?.length === 0) {
      openModalConfirm();
      return;
    }

    openModal();
  };

  const handleRegistration = () => {
    if (selectedEvent.eventAreas?.length && selectedAreas.length === 0) {
      setSelectedAreasError('Debes seleccionar al menos una área');
      return;
    }

    createRegistration({
      event: selectedEvent.id,
      preferredEventAreas: selectedAreas,
    })
      .then((registration) => {
        closeModal();
        closeModalConfirm();

        setEvents(
          events.map((event) =>
            event.id === selectedEvent.id
              ? { ...event, userAlreadyRegistered: true }
              : event
          )
        );

        registration.event = selectedEvent;

        setRegistrations([...registrations, registration]);

        addToast(
          <ToastRoot>
            <Alert
              variant="success"
              direction="column"
              title={`Te has inscrito correctamente a "${selectedEvent.fullName}"`}
              text="En breve te contactaran los organizadores"
            />
          </ToastRoot>
        );
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al inscribirte a "${selectedEvent.fullName}"`}
              text="Ha ocurrido un error al inscribirte en este evento. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );
        console.error(error);
      });
  };

  return (
    <>
      <div className={classNames(styles.card, className)}>
        {mobileEventsOpen && <div className={styles.backgroundBlur} />}
        <img
          src={CardBackground}
          alt="Card Background"
          className={styles.card__background}
        />
        <div className={styles.card__content}>
          <div
            className={styles.card__header}
            onClick={() => setMobileEventsOpen(!mobileEventsOpen)}
          >
            <Heading as="h2" variant="heading-1">
              Eventos disponibles para inscribirte
            </Heading>
            <Button
              variant="solid"
              color="accent"
              size="md"
              isOnlyIcon
              onClick={() => setMobileEventsOpen(!mobileEventsOpen)}
              className={styles.card__headerButton}
            >
              {mobileEventsOpen ? <AngleDownIcon /> : <AngleUpIcon />}
            </Button>
          </div>
          <div
            className={styles.card__body}
            data-mobile-open={mobileEventsOpen}
          >
            {isLoading ? (
              <p>Cargando eventos...</p>
            ) : events.length > 0 ? (
              <ul className={styles.card__eventsList}>
                {events.map((event, index) => (
                  <div key={event.id}>
                    <li className={styles.card__event}>
                      <div className={styles.card__eventContainer}>
                        <img
                          src={IMAGES_MAP[event.eventType.name] || DefaultLogo}
                          alt={event.fullName}
                          className={styles.card__eventLogo}
                        />
                        <div className={styles.card__eventInfo}>
                          <Text variant="body-2">
                            {printEventDate(event.startEvent, event.endEvent)}
                          </Text>
                          <Heading as="h4" variant="heading-2">
                            <span>{event.fullName}</span>
                          </Heading>
                        </div>
                      </div>
                      <div className={styles.card__eventButton}>
                        {event.userAlreadyRegistered ? (
                          <Button
                            variant="solid"
                            color="accent"
                            size="md"
                            disabled
                          >
                            Inscrito
                          </Button>
                        ) : (
                          <Button
                            variant="solid"
                            color="primary"
                            size="md"
                            onClick={() => handleEventClick(event)}
                          >
                            Inscribirme
                          </Button>
                        )}
                      </div>
                    </li>
                    {index < events.length - 1 && (
                      <div className={styles.card__eventSeparator} />
                    )}
                  </div>
                ))}
              </ul>
            ) : (
              <Alert variant="warning" title="No hay eventos disponibles" />
            )}
          </div>
        </div>
      </div>
      <Modal isOpen={isOpen} onClose={closeModal} size="md">
        <ModalHeader closeModal={closeModal}>
          Antes de inscribirte...
        </ModalHeader>
        <ModalBody>
          <div className={styles.modal__content}>
            <div className={styles.modal__eventInfo}>
              <Heading as="h5" variant="subhead">
                <strong>¿En qué áreas preferirías participar?</strong>
              </Heading>
              <Text variant="body-2">
                Tus preferencias serán tenidas en cuenta, y la asignación de
                área se basará en tu perfil, características, disponibilidad y
                preferencias personales,{' '}
                <b>
                  pero no se garantiza que el área asignada esté entre las
                  elegidas
                </b>
                .
              </Text>
            </div>
            {selectedAreasError && (
              <Alert variant="error" title={selectedAreasError} />
            )}
            <div className={styles.modal__areasList}>
              {selectedEvent.eventAreas?.map((area) => (
                <Checkbox
                  key={area.id}
                  id={`area-${area.id}`}
                  label={area.name}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedAreas([...selectedAreas, area.id]);
                    } else {
                      setSelectedAreas(
                        selectedAreas.filter(
                          (selectedArea) => selectedArea !== area.id
                        )
                      );
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="solid" color="primary" onClick={handleRegistration}>
            Confirmar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={isOpenConfirm} onClose={closeModalConfirm} size="md">
        <ModalHeader closeModal={closeModalConfirm}>
          Confirmar inscripción
        </ModalHeader>
        <ModalBody>
          <div className={styles.modal__content}>
            <Heading as="h5" variant="subhead">
              Estás a punto de inscribirte en el siguiente evento:
            </Heading>
            <Text variant="body-2">{selectedEvent.fullName}</Text>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="solid" color="primary" onClick={handleRegistration}>
            Confirmar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
